import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { NAV } from '@/constants/testIds';
import {
  ChartLineUp, Receipt, Package, Users, Stethoscope, BookOpen,
  Wallet, ChartBar, SignOut, List, X, UserCircle, CaretDown, CaretRight,
  FirstAidKit, Truck, MapTrifold, Gear, CurrencyCircleDollar
} from '@phosphor-icons/react';

const menuItems = [
  { key: 'dashboard', label: 'Dashboard', icon: ChartLineUp, path: '/admin' },
  { key: 'accounts', label: 'Accounts', icon: CurrencyCircleDollar, path: '/admin/billing',
    children: [
      { key: 'new-sale', label: 'New Sale', path: '/admin/billing/new' },
      { key: 'sale-book', label: 'Sale Book', path: '/admin/billing' },
      { key: 'sale-return', label: 'Sale Return', path: '/admin/billing/returns' },
      { key: 'purchase', label: 'Purchase', path: '/admin/purchase' },
      { key: 'inventory', label: 'Inventory', path: '/admin/inventory' },
    ]
  },
  { key: 'customers', label: 'Doctor CRM', icon: Users, path: '/admin/customers' },
  { key: 'doctor-mgmt', label: 'Doctor Management', icon: Stethoscope, path: '/admin/doctors',
    children: [
      { key: 'doctors', label: 'Doctors', path: '/admin/doctors' },
      { key: 'doctor-panel', label: 'Doctor Panel', path: '/admin/doctor-panel' },
      { key: 'beats', label: 'Beat Management', path: '/admin/beats' },
    ]
  },
  { key: 'khata', label: 'Khata Book', icon: BookOpen, path: '/admin/khata' },
  { key: 'reports', label: 'Reports', icon: ChartBar, path: '/admin/reports' },
  { key: 'users', label: 'Staff', icon: UserCircle, path: '/admin/users' },
  { key: 'master', label: 'Master Settings', icon: Gear, path: '/admin/master' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#F9F9FA' }}>
      {/* Sidebar */}
      <aside
        data-testid={NAV.sidebar}
        className="flex flex-col border-r transition-all duration-200"
        style={{
          width: collapsed ? 64 : 240,
          background: '#FFFFFF',
          borderColor: '#E2E4E9',
          minHeight: '100vh'
        }}
      >
        {/* Logo */}
        <div className="flex items-center gap-2 px-4 h-14 border-b" style={{ borderColor: '#E2E4E9' }}>
          <div className="w-8 h-8 rounded flex items-center justify-center shrink-0" style={{ background: '#0A2540' }}>
            <Package size={16} weight="duotone" color="white" />
          </div>
          {!collapsed && (
            <span className="text-sm font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>
              PharmaPro
            </span>
          )}
          <button
            data-testid={NAV.sidebarToggle}
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto p-1 rounded hover:bg-slate-50 transition-colors"
          >
            {collapsed ? <List size={18} /> : <X size={18} />}
          </button>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const hasChildren = item.children?.length > 0;
            const isExpanded = expanded[item.key];

            return (
              <div key={item.key}>
                <button
                  data-testid={NAV[`nav${item.key.charAt(0).toUpperCase() + item.key.slice(1)}`] || `nav-${item.key}`}
                  onClick={() => {
                    if (hasChildren) {
                      toggleExpand(item.key);
                      if (!isExpanded) navigate(item.path);
                    } else {
                      navigate(item.path);
                    }
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors"
                  style={{
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    fontWeight: active ? 600 : 400,
                    color: active ? '#0A2540' : '#8A94A6',
                    background: active ? '#F0F4F8' : 'transparent',
                    borderRight: active ? '2px solid #007AFF' : '2px solid transparent',
                  }}
                >
                  <Icon size={20} weight={active ? 'duotone' : 'regular'} />
                  {!collapsed && (
                    <>
                      <span className="flex-1">{item.label}</span>
                      {hasChildren && (isExpanded ? <CaretDown size={14} /> : <CaretRight size={14} />)}
                    </>
                  )}
                </button>
                {hasChildren && isExpanded && !collapsed && (
                  <div className="ml-9">
                    {item.children.map(child => (
                      <button
                        key={child.key}
                        onClick={() => navigate(child.path)}
                        className="w-full text-left px-3 py-2 text-xs transition-colors"
                        style={{
                          fontFamily: "'IBM Plex Sans', sans-serif",
                          color: location.pathname === child.path ? '#007AFF' : '#8A94A6',
                          fontWeight: location.pathname === child.path ? 600 : 400,
                        }}
                      >
                        {child.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Info + Logout */}
        <div className="border-t px-4 py-3" style={{ borderColor: '#E2E4E9' }}>
          {!collapsed && (
            <div data-testid={NAV.userInfo} className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: '#007AFF' }}>
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div>
                <p className="text-xs font-semibold" style={{ color: '#0A2540' }}>{user?.name || 'Admin'}</p>
                <p className="text-[10px]" style={{ color: '#8A94A6' }}>{user?.role?.toUpperCase()}</p>
              </div>
            </div>
          )}
          <button
            data-testid={NAV.navLogout}
            onClick={logout}
            className="w-full flex items-center gap-2 px-2 py-2 rounded text-xs font-medium transition-colors hover:bg-red-50"
            style={{ color: '#FF3B30' }}
          >
            <SignOut size={18} weight="duotone" />
            {!collapsed && 'Sign Out'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
